import { router } from '@inertiajs/react';
import { Play } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { SharedProps } from '@/types';

interface ForceRunButtonProps {
  jobName: string;
  isOneOff?: boolean;
  description?: string | null;
  returnsDownload?: boolean;
  acceptsCsvUpload?: boolean;
  acceptsJsonUpload?: boolean;
  eventSlug?: string | null;
}

// A job that answers with a file cannot go through Inertia: the browser has to receive
// the response itself to save it. A throwaway native form does exactly that, and the
// server names the file (`<job>-<date>.csv`) the way the Next UI named the blob.
function submitAsNativeForm(action: string, fields: Record<string, string>) {
  const token =
    document
      .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
      ?.getAttribute('content') ?? '';

  const form = document.createElement('form');
  form.method = 'post';
  form.action = action;
  form.style.display = 'none';

  for (const [name, value] of Object.entries({
    ...fields,
    authenticity_token: token,
  })) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
}

export function ForceRunButton({
  jobName,
  isOneOff = false,
  description,
  returnsDownload = false,
  acceptsCsvUpload = false,
  acceptsJsonUpload = false,
  eventSlug,
}: ForceRunButtonProps) {
  const [isPending, setIsPending] = useState(false);
  const [open, setOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const resetUpload = () => {
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const runJob = (uploadedText?: string) => {
    const action = `/admin/cron/jobs/${jobName}/run`;
    const payload = {
      ...(uploadedText
        ? acceptsJsonUpload
          ? { json: uploadedText }
          : { csv: uploadedText }
        : {}),
      ...(eventSlug ? { event_slug: eventSlug } : {}),
    };

    if (returnsDownload) {
      submitAsNativeForm(action, payload);
      setOpen(false);
      resetUpload();
      return;
    }

    setIsPending(true);
    router.post(action, payload, {
      preserveScroll: true,
      onSuccess: (page) => {
        const { flash } = page.props as unknown as SharedProps;

        if (flash.alert) {
          toast.error(flash.alert);
        } else {
          toast.success(flash.notice ?? 'Job executed successfully');
        }
      },
      onError: () => toast.error('Failed to run job'),
      onFinish: () => {
        setIsPending(false);
        setOpen(false);
        resetUpload();
      },
    });
  };

  const handleForceRun = async () => {
    if (acceptsCsvUpload || acceptsJsonUpload) {
      if (!uploadedFile) {
        toast.error(
          acceptsJsonUpload
            ? 'Please select a JSON file'
            : 'Please select a CSV file',
        );
        return;
      }
      const text = await uploadedFile.text();
      runJob(text);
      return;
    }
    runJob();
  };

  const runButton = (
    <Button
      size="sm"
      variant="outline"
      disabled={isPending}
      onClick={isOneOff ? undefined : () => runJob()}
    >
      <Play className="h-4 w-4" />
      {isPending ? 'Running...' : 'Run Now'}
    </Button>
  );

  if (!isOneOff) {
    return runButton;
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) resetUpload();
      }}
    >
      <AlertDialogTrigger asChild>{runButton}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Run One-Off Action</AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <span className="block">
              Are you sure you want to run this one-off action? This action
              cannot be undone.
            </span>
            <span className="block rounded-md bg-muted p-3">
              <span className="block font-medium text-foreground">
                {jobName}
              </span>
              {description && (
                <span className="mt-1 block text-muted-foreground text-sm">
                  {description}
                </span>
              )}
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        {(acceptsCsvUpload || acceptsJsonUpload) && (
          <div className="flex flex-col gap-2">
            <Label htmlFor={`${jobName}-file-upload`}>
              {acceptsJsonUpload ? 'JSON file' : 'CSV file'}
            </Label>
            <Input
              id={`${jobName}-file-upload`}
              ref={fileInputRef}
              type="file"
              accept={
                acceptsJsonUpload ? '.json,application/json' : '.csv,text/csv'
              }
              disabled={isPending}
              onChange={(e) => setUploadedFile(e.target.files?.[0] ?? null)}
            />
            {uploadedFile && (
              <p className="text-muted-foreground text-xs">
                {uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleForceRun}
            disabled={
              isPending ||
              ((acceptsCsvUpload || acceptsJsonUpload) && !uploadedFile)
            }
          >
            {isPending ? 'Running...' : 'Run Action'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
